import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMenus,
  bulkCreateMenus,
  updateMenu,
  deleteMenu,
  fetchIngredients,
  bulkCreateIngredients,
  updateIngredient,
  deleteIngredient,
  clearError,
  setSearchTerm,
  toggleMenuStock,
} from '../../store/slices/canteenSlice';
import { fetchSubcategories } from '../../store/slices/subcategorySlice';
import {
  fetchOrders,
  placeOrder,
  cancelOrder,
  clearError as clearOrderError,
} from '../../store/slices/orderSlice';
import orderApi from 'api/orderApi';
import {
  Plus, Minus, Trash2, Pencil, MoreHorizontal, Loader2, Package, X, ChevronDown, ChevronUp,
  Search, ShoppingCart, Clock, CheckCircle, XCircle, IndianRupee, Star, AlertCircle,
  LocateIcon,
  Upload,
  Ban,
  RotateCcw,
  QrCode
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../../components/ui/sheet';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { usePermissions } from '../../hooks/usePermissions';
import usePermissionSocket from '../../hooks/usePermissionSocket';
import useCanteenSocket from 'hooks/useCanteenSocket';
import { fetchCanteenDropdown, restoreRole } from 'store/slices/commonSlice';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from 'lib/utils';
import { fetchSettings, updateSettings } from 'store/slices/settingsSlice';
import profileApi from 'api/profileApi';
import OrderStats from 'components/common/OrderStats';
import { canManageEverything } from 'utlis/authUtils';
import qrcode from 'assets/images/qrcode.jpg'


const statusConfig = {
  pending: { label: 'Pending', variant: 'secondary', icon: Clock },
  preparing: { label: 'Preparing', variant: 'default', icon: Package },
  ready: { label: 'Ready', variant: 'outline', icon: CheckCircle },
  delivered: { label: 'Delivered', variant: 'success', icon: CheckCircle },
  cancelled: { label: 'Cancelled', variant: 'destructive', icon: XCircle },
};

const CanteenPage = () => {
  useCanteenSocket();
  usePermissionSocket();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isSuperAdmin = canManageEverything();

  const { menus, isLoading, error, searchTerm } = useSelector((s) => s.canteen);
  const subcategories = useSelector((state) => state.common.subcategories.items);
  const { stats: orderStats = {
    pending: 0,
    preparing: 0,
    ready: 0,
    today_total: 0,
    today_revenue: 0,
    delivered: 0,
    cancelled: 0,
    today_total_pending: 0,
    today_total_preparing: 0,
    today_total_ready: 0,
    today_total_delivered: 0,
    today_total_cancelled: 0,
    monthly_total: 0,
    monthly_total_delivered: 0,
    monthly_total_cancelled: 0,
    monthly_revenue: 0,
    total_revenue: 0
  }, orders, isLoading: orderLoading, error: orderError } = useSelector((s) => s.order);

  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('menu');
  const { canRead: canReadIngredients, canCreate: canCreateIngredients, canUpdate: canUpdateIngredients, canDelete: canDeleteIngredients } = usePermissions('ingredients');
  const { canRead: canReadCanteenLog } = usePermissions('canteen_order_log')
  const { can_accept_orders, isLoading: settingsLoading } = useSelector((state) => state.settings);

  const handleToggleAcceptingOrders = async (checked) => {
    try {
      await dispatch(updateSettings({ can_accept_orders: checked })).unwrap();
      toast.success(`Orders are now ${checked ? 'being accepted' : 'paused'}`);
    } catch (err) {
      toast.error('Failed to update order acceptance');
    }
  }

  const handleViewAllOrders = (e) => {
    if (!canReadCanteenLog) {
      e.preventDefault();
      toast.error("Access Denied", {
        description: "You don't have permission to view the full order log.",
        duration: 5000,
        icon: <LocateIcon className="w-4 h-4" />,
      });
      return;
    }
    navigate('/order-list');
  };

  // Menu States
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkMenus, setBulkMenus] = useState([
    { name: '', description: '', price: '', in_stock: true, profile_img: null, preview: '' },
  ]);
  const [bulkMenuErrors, setBulkMenuErrors] = useState([{}]);

  const [editMenu, setEditMenu] = useState(null);
  const [editMenuErrors, setEditMenuErrors] = useState({});
  const [menuImageFile, setMenuImageFile] = useState(null);
  const [menuImagePreview, setMenuImagePreview] = useState('');

  const [ingredientMenuId, setIngredientMenuId] = useState(null);
  const [bulkIngredients, setBulkIngredients] = useState([{ ingredient: '', quantity: '' }]);
  const [ingredientErrors, setIngredientErrors] = useState([{}]);

  const [editIngredient, setEditIngredient] = useState(null);
  const [editIngredientErrors, setEditIngredientErrors] = useState({});

  const [deleteMenuId, setDeleteMenuId] = useState(null);
  const [deleteIngredientId, setDeleteIngredientId] = useState(null);

  // Add this near other useState calls
  const [walletModalOpen, setWalletModalOpen] = useState(false);

  // Cart & Orders
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [orderDetails, setOrderDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});

  const [toggleLoading, setToggleLoading] = useState({});

  const [isSearching, setIsSearching] = useState(false);
  const [canManageOrder, setCanManageOrder] = useState(false);

  // NEW: State for Restore confirmation
  const [restoreMenuId, setRestoreMenuId] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  useEffect(() => {
    if (!canRead) return;

    setIsSearching(true);

    const timer = setTimeout(() => {
      dispatch(fetchMenus()).finally(() => setIsSearching(false));
    }, searchTerm ? 400 : 0);

    if (!searchTerm) setIsSearching(false);

    return () => clearTimeout(timer);
  }, [searchTerm, dispatch, canRead]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await profileApi.getMyProfile();
        setCanManageOrder(profile?.can_manage_orders);
      } catch (err) {
        console.error('Failed to load profile for canteen permissions', err);
      }
    };
    loadProfile();
  }, []);

  useEffect(() => {
    if (canRead) {
      dispatch(fetchMenus());
      dispatch(fetchCanteenDropdown());
      dispatch(fetchOrders({ page: 1, pageSize: 10, forCurrentUser: true }));
    }
  }, [dispatch, canRead]);

  useEffect(() => {
    if (error) { toast.error(error); dispatch(clearError()); }
    if (orderError) { toast.error(orderError); dispatch(clearOrderError()); }
  }, [error, orderError, dispatch]);

  const toggleExpand = (menuId) => {
    const newSet = new Set(expandedMenus);
    if (newSet.has(menuId)) {
      newSet.delete(menuId);
    } else {
      newSet.add(menuId);
      const menu = menus.find((m) => m.id === menuId);
      if (!menu?.ingredients) dispatch(fetchIngredients(menuId));
    }
    setExpandedMenus(newSet);
  };

  // restore data
  // Restore handler with confirmation
  const handleRestoreClick = (menuId) => {
    setRestoreMenuId(menuId);
  };

  const confirmRestore = async () => {
    if (!restoreMenuId) return;

    setRestoreLoading(true);
    try {
      // Correct: Use the generic restoreEntity with proper payload
      await dispatch(restoreRole({
        entityType: 'menus',  // plural, as per your backend route pattern
        id: restoreMenuId
      })).unwrap();

      toast.success('Menu restored successfully');
      setRestoreMenuId(null);
      // Optional: refetch menus to reflect restored item
      dispatch(fetchMenus());
    } catch (err) {
      console.error('Restore failed:', err);
      toast.error(err || 'Failed to restore menu');
    } finally {
      setRestoreLoading(false);
    }
  };

  const addToCart = (menu) => {
    if (!menu.in_stock) return toast.error(`${menu.name} is out of stock`);
    setCart(prev => {
      const existing = prev.find(i => i.id === menu.id);
      if (existing) return prev.map(i => i.id === menu.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...menu, qty: 1 }];
    });
    toast.success(`${menu.name} added to cart`);
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev
      .map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i)
      .filter(i => i.qty > 0)
    );
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(i => i.id !== id));
    toast.success('Item removed');
  };

  const getTotal = () => cart.reduce((sum, i) => sum + i.price * i.qty, 0).toFixed(2);

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return toast.error('Cart is empty');
    if (isPlacingOrder) return;
    const items = cart.map(i => ({ menu: i.id, quantity: i.qty }));
    try {
      await dispatch(placeOrder(items)).unwrap();
      toast.success('Order placed successfully!');
      setCart([]);
      setIsCartOpen(false);
      dispatch(fetchOrders({ page: 1, pageSize: 200 }));
    } catch (err) {
      console.log("ORDER ERROR:", err);
      toast.error(typeof err === "string" ? err : "Failed to place order");
    }
    finally {
      setIsPlacingOrder(false); // Always re-enable the button
    }
  };

  const toggleOrderDetails = async (orderId) => {
    const newSet = new Set(expandedOrders);
    if (newSet.has(orderId)) {
      newSet.delete(orderId);
    } else {
      newSet.add(orderId);
      if (!orderDetails[orderId]) {
        setDetailsLoading(prev => ({ ...prev, [orderId]: true }));
        try {
          const data = await orderApi.getById(orderId);
          setOrderDetails(prev => ({ ...prev, [orderId]: data }));
        } catch (err) {
          console.error('Failed to fetch order details:', err);
          toast.error('Failed to load order details');
        } finally {
          setDetailsLoading(prev => ({ ...prev, [orderId]: false }));
        }
      }
    }
    setExpandedOrders(newSet);
  };

  const handleImageChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image');
      return;
    }
    setFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Validation Helpers
  const validateBulkMenus = () => {
    const errors = bulkMenus.map(menu => {
      const err = {};
      if (!menu.name.trim()) err.name = 'Name is required';
      if (!menu.price || isNaN(menu.price) || Number(menu.price) <= 0) err.price = 'Valid price is required';
      return err;
    });
    setBulkMenuErrors(errors);
    return errors.every(err => Object.keys(err).length === 0);
  };

  const validateEditMenu = () => {
    const err = {};
    if (!editMenu.name.trim()) err.name = 'Name is required';
    if (!editMenu.price || isNaN(editMenu.price) || Number(editMenu.price) <= 0) err.price = 'Valid price is required';
    setEditMenuErrors(err);
    return Object.keys(err).length === 0;
  };

  const validateBulkIngredients = () => {
    const errors = bulkIngredients.map(ing => {
      const err = {};
      if (!ing.ingredient) err.ingredient = 'Select an ingredient';
      if (!ing.quantity || Number(ing.quantity) <= 0) err.quantity = 'Valid quantity required';
      return err;
    });
    setIngredientErrors(errors);
    return errors.every(err => Object.keys(err).length === 0);
  };

  const validateEditIngredient = () => {
    const err = {};
    if (!editIngredient.ingredient) err.ingredient = 'Select an ingredient';
    if (!editIngredient.quantity || Number(editIngredient.quantity) <= 0) err.quantity = 'Valid quantity required';
    setEditIngredientErrors(err);
    return Object.keys(err).length === 0;
  };

  const addBulkMenuRow = () => {
    setBulkMenus(prev => [...prev, { name: '', description: '', price: '', in_stock: true, profile_img: null, preview: '' }]);
    setBulkMenuErrors(prev => [...prev, {}]);
  };

  const updateBulkMenu = (i, field, value) => {
    setBulkMenus(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
    setBulkMenuErrors(prev => {
      const newErrors = [...prev];
      if (newErrors[i]) delete newErrors[i][field];
      return newErrors;
    });
  };

  const handleBulkImage = (i, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Invalid image');
    const reader = new FileReader();
    reader.onloadend = () => {
      setBulkMenus(prev => prev.map((m, idx) => idx === i ? { ...m, profile_img: file, preview: reader.result } : m));
    };
    reader.readAsDataURL(file);
  };

  const submitBulkMenus = async () => {
    if (!canCreate) return toast.error("No permission");
    if (!validateBulkMenus()) return;

    const valid = bulkMenus.filter(m => m.name.trim() && m.price > 0);
    if (!valid.length) return toast.error('Add at least one valid menu');

    const payload = valid.map(m => ({
      name: m.name.trim(),
      description: m.description?.trim() || '',
      price: parseFloat(m.price),
      in_stock: m.in_stock,
      profile_img: m.profile_img,
    }));

    try {
      await dispatch(bulkCreateMenus(payload)).unwrap();
      toast.success('Menus created successfully');
      setBulkMenuOpen(false);
      setBulkMenus([{ name: '', description: '', price: '', in_stock: true, profile_img: null, preview: '' }]);
      setBulkMenuErrors([{}]);
    }
    catch (err) {
      console.error("check", err, err === "Canteen Menu with this name already exists.");

      toast.error(err === "Canteen Menu with this name already exists." ? "Menu name already exists. Please use a unique name or contact Super Admin." : err || 'Failed to create menus');
    }
  };

  const openEditMenu = (menu) => {
    if (!canUpdate) return toast.error("No permission");
    setEditMenu({ ...menu });
    setMenuImageFile(null);
    setMenuImagePreview(menu.profile_img || '');
    setEditMenuErrors({});
  };

  const saveMenu = async () => {
    if (!validateEditMenu()) return;

    const payload = {
      name: editMenu.name.trim(),
      description: editMenu.description ?? '',
      price: parseFloat(editMenu.price),
      in_stock: editMenu.in_stock,
    };

    if (menuImageFile instanceof File) {
      payload.profile_img = menuImageFile;
    } else if (menuImageFile === '') {
      payload.profile_img = '';
    }

    try {
      await dispatch(updateMenu({ id: editMenu.id, data: payload })).unwrap();
      toast.success('Menu updated successfully');
      setEditMenu(null);
      setMenuImageFile(null);
      setMenuImagePreview('');
      setEditMenuErrors({});
    } catch {
      toast.error('Failed to update menu');
    }
  };

  const addIngredientRow = () => {
    setBulkIngredients(prev => [...prev, { ingredient: '', quantity: '' }]);
    setIngredientErrors(prev => [...prev, {}]);
  };

  const updateIngredientRow = (i, field, value) => {
    setBulkIngredients(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
    setIngredientErrors(prev => {
      const newErrors = [...prev];
      if (newErrors[i]) delete newErrors[i][field];
      return newErrors;
    });
  };

  const submitBulkIngredients = async () => {
    if (!canCreateIngredients) return toast.error("No permission");
    if (!validateBulkIngredients()) return;

    const valid = bulkIngredients.filter(i => i.ingredient && i.quantity > 0);
    if (!valid.length) return toast.error('Add at least one ingredient');

    const payload = valid.map(i => ({
      menu: ingredientMenuId,
      ingredient: i.ingredient,
      quantity: i.quantity
    }));

    try {
      await dispatch(bulkCreateIngredients(payload)).unwrap();
      toast.success('Ingredients added');
      setIngredientMenuId(null);
      setBulkIngredients([{ ingredient: '', quantity: '' }]);
      setIngredientErrors([{}]);
    } catch {
      toast.error('Failed to add ingredients');
    }
  };

  const openEditIngredient = (ing) => {
    if (!canUpdateIngredients) return toast.error("No permission");
    setEditIngredient({ ...ing });
    setEditIngredientErrors({});
  };

  // Restore handler
  const handleRestoreMenu = async (menuId) => {
    try {
      await dispatch(updateMenu({ id: menuId, data: { is_deleted: false } })).unwrap();
      toast.success('Menu restored successfully');
    } catch {
      toast.error('Failed to restore menu');
    }
  };

  // Permanent delete confirmation
  const handlePermanentDelete = (menuId) => {
    setDeleteMenuId(menuId);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Canteen"
        description="Order food & manage menu"
        action={canCreate ? {
          label: 'Add Menus',
          onClick: () => setBulkMenuOpen(true),
          icon: <Plus className="h-4 w-4" />
        } : null}

        secondaryAction={{
          label: 'Recharge Wallet',
          onClick: () => setWalletModalOpen(true),
          icon: (
            <div className="relative w-5 h-5 flex items-center justify-center">
              <QrCode className="h-4 w-4 relative z-10" />
              {/* Scanning laser line */}
              <div className="absolute inset-0 overflow-hidden rounded">
                <div className="absolute w-full h-[2px] bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-[scan_2s_ease-in-out_infinite]"></div>
              </div>
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-1.5 h-1.5 border-t-2 border-l-2 border-red-500"></div>
              <div className="absolute top-0 right-0 w-1.5 h-1.5 border-t-2 border-r-2 border-red-500"></div>
              <div className="absolute bottom-0 left-0 w-1.5 h-1.5 border-b-2 border-l-2 border-red-500"></div>
              <div className="absolute bottom-0 right-0 w-1.5 h-1.5 border-b-2 border-r-2 border-red-500"></div>
            </div>
          ),
        }}
      />

      {canReadCanteenLog && <OrderStats orderStats={orderStats} totalOrders={orders.length} />}

      <Tabs defaultValue="menu" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="menu">Menu</TabsTrigger>
          <TabsTrigger value="orders">Recent Orders</TabsTrigger>
        </TabsList>

        {/* MENU TAB */}
        <TabsContent value="menu" className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="relative flex-1 sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search food..."
                value={searchTerm}
                onChange={(e) => dispatch(setSearchTerm(e.target.value))}
                className="pl-10 bg-background"
              />
            </div>

            <div className="flex items-center gap-4 w-full sm:w-auto">
              {(canManageOrder || isSuperAdmin) &&
                <div className="flex items-center gap-3 bg-card px-4 py-2 rounded-lg border">
                  {settingsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <Switch
                    checked={can_accept_orders}
                    onCheckedChange={handleToggleAcceptingOrders}
                    disabled={settingsLoading || !canUpdate}
                    id="accept-orders-toggle"
                  />
                  <Label
                    htmlFor="accept-orders-toggle"
                    className={cn(
                      "font-semibold whitespace-nowrap",
                      can_accept_orders ? "text-green-600" : "text-destructive"
                    )}
                  >
                    {can_accept_orders ? "Accepting Orders" : "Orders Paused"}
                  </Label>
                </div>}

              <Button
                onClick={() => setIsCartOpen(true)}
                disabled={!can_accept_orders || cart.length === 0}
                className="relative min-w-[140px]"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Cart ({cart.reduce((s, i) => s + i.qty, 0)})
                {cart.length > 0 && (
                  <Badge className="absolute -top-2 -right-2 animate-pulse">
                    {cart.reduce((s, i) => s + i.qty, 0)}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {isSearching ? (
            <div className="text-center py-16">
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
              <p className="mt-4 text-lg text-muted-foreground font-medium animate-pulse">
                Searching for "<span className="text-foreground">{searchTerm}</span>"...
              </p>
            </div>
          ) : menus.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-4">
                <Package className="h-10 w-10 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                {searchTerm ? 'No menu items found' : 'No menu items yet'}
              </p>
              {canCreate && !searchTerm && (
                <Button className="mt-4" onClick={() => setBulkMenuOpen(true)}>
                  Add Your First Menu Item
                </Button>
              )}
            </div>
          ) : can_accept_orders ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {menus.map((menu) => {
                const isDeleted = menu.is_deleted === true;

                return (
                  <Card
                    key={menu.id}
                    className={cn(
                      "overflow-hidden transition-all hover:shadow-lg h-fit relative",
                      isDeleted && "opacity-70 ring-4 ring-red-200"
                    )}
                  >
                    {/* Deleted overlay */}
                    {isDeleted && (
                      <div className="absolute inset-0 bg-red-500/20 z-10 pointer-events-none" />
                    )}

                    <div className="relative">
                      {menu.profile_img ? (
                        <img
                          src={menu.profile_img}
                          alt={menu.name}
                          className="w-full h-[400px] object-cover"
                        />
                      ) : (
                        <div className="w-full h-[400px] bg-muted flex items-center justify-center">
                          <Package className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}

                      <Badge
                        variant={menu.in_stock ? "default" : "secondary"}
                        className="absolute top-2 left-2"
                      >
                        {menu.in_stock ? "In Stock" : "Out of Stock"}
                      </Badge>

                      {/* Deleted info badge */}
                      {isDeleted && (
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          <Badge variant="destructive" className="text-xs">
                            Deleted
                          </Badge>
                          {menu.deleted_by_name && (
                            <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                              By: {menu.deleted_by_name}
                            </span>
                          )}
                          {menu.deleted_at && (
                            <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                              On: {new Date(menu.deleted_at).toLocaleDateString('en-GB')}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Admin controls */}
                      {(canUpdate || canDelete) && (
                        <div className="absolute top-2 right-2 z-20">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="secondary" size="icon" className="h-8 w-8 bg-white/90 backdrop-blur hover:bg-white">
                                <MoreHorizontal className="h-4 w-4 text-black" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {/* Normal actions for active menus */}
                              {!isDeleted && (
                                <>
                                  {canUpdate && (
                                    <DropdownMenuItem onClick={() => openEditMenu(menu)}>
                                      <Pencil className="mr-2 h-4 w-4" /> Edit
                                    </DropdownMenuItem>
                                  )}
                                  {canCreateIngredients && (
                                    <DropdownMenuItem onClick={() => setIngredientMenuId(menu.id)}>
                                      <Plus className="mr-2 h-4 w-4" /> Add Ingredients
                                    </DropdownMenuItem>
                                  )}
                                  {canDelete && (
                                    <DropdownMenuItem
                                      onClick={() => handlePermanentDelete(menu.id)}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}

                              {/* Restore & Permanent Delete for deleted menus */}
                              {isDeleted && (
                                <>
                                  {/* {canUpdate && (
                                    <DropdownMenuItem
                                      onClick={() => handleRestoreMenu(menu.id)}
                                      className="text-green-600"
                                    >
                                      <RotateCcw className="mr-2 h-4 w-4" /> Restore
                                    </DropdownMenuItem>
                                  )} */}
                                  {canUpdate && (
                                    <DropdownMenuItem
                                      onClick={() => handleRestoreClick(menu.id)}
                                      className="text-green-600"
                                    >
                                      <RotateCcw className="mr-2 h-4 w-4" /> Restore
                                    </DropdownMenuItem>
                                  )}
                                  {canDelete && (
                                    <DropdownMenuItem
                                      onClick={() => handlePermanentDelete(menu.id)}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" /> Permanent Delete
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>

                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div>
                          <h3 className="font-semibold text-lg truncate">{menu.name}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">{menu.description}</p>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold">₹{menu.price}</span>

                          {canUpdate && !isDeleted && (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={menu.in_stock}
                                onCheckedChange={async () => {
                                  setToggleLoading(prev => ({ ...prev, [menu.id]: true }));
                                  try {
                                    await dispatch(toggleMenuStock(menu.id)).unwrap();
                                  } finally {
                                    setToggleLoading(prev => ({ ...prev, [menu.id]: false }));
                                  }
                                }}
                                disabled={toggleLoading[menu.id]}
                                id={`stock-${menu.id}`}
                              />
                              <Label htmlFor={`stock-${menu.id}`} className="text-sm">
                                {menu.in_stock ? "Available" : "Unavailable"}
                              </Label>
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2 pt-2 flex-col xl:flex-row">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => !isDeleted && toggleExpand(menu.id)}
                            disabled={isDeleted}
                            title={isDeleted ? "Cannot view ingredients of deleted menu" : undefined}
                          >
                            {expandedMenus.has(menu.id) ? (
                              <>Hide Ingredients <ChevronUp className="ml-2 h-4 w-4" /></>
                            ) : (
                              <>View Ingredients <ChevronDown className="ml-2 h-4 w-4" /></>
                            )}
                          </Button>

                          {!isDeleted && menu.in_stock && (
                            <Button
                              className="flex-1"
                              onClick={() => addToCart(menu)}
                            >
                              Add to Cart
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Ingredients section */}
                      {expandedMenus.has(menu.id) && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium">Ingredients</h4>
                            {canCreateIngredients && !isDeleted && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setIngredientMenuId(menu.id)}
                              >
                                <Plus className="mr-1 h-4 w-4" /> Add
                              </Button>
                            )}
                          </div>

                          {menu.ingredients === null ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                          ) : menu.ingredients?.length > 0 ? (
                            <div className="space-y-2">
                              {menu.ingredients.map((ing) => (
                                <div key={ing.id} className="flex items-center justify-between p-2 rounded-md border">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                      <span className="text-xs font-medium">
                                        {ing.ingredient_name.charAt(0)}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="font-medium">{ing.ingredient_name}</p>
                                      <p className="text-xs text-muted-foreground">Quantity: {ing.quantity}</p>
                                    </div>
                                  </div>

                                  {(canUpdateIngredients || canDeleteIngredients) && !isDeleted && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {canUpdateIngredients && (
                                          <DropdownMenuItem onClick={() => openEditIngredient(ing)}>
                                            <Pencil className="mr-2 h-4 w-4" /> Edit
                                          </DropdownMenuItem>
                                        )}
                                        {canDeleteIngredients && (
                                          <DropdownMenuItem
                                            onClick={() => setDeleteIngredientId(ing.id)}
                                            className="text-destructive"
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                              <p>No ingredients added</p>
                              {canCreateIngredients && !isDeleted && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2"
                                  onClick={() => setIngredientMenuId(menu.id)}
                                >
                                  <Plus className="mr-1 h-4 w-4" /> Add First Ingredient
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-2xl p-8 text-center shadow-2xl w-full">
              <Ban className="h-20 w-20 mx-auto mb-4" />
              <h2 className="text-4xl font-bold mb-3">Canteen is Closed</h2>
              <p className="text-xl opacity-90">We are not accepting orders at the moment</p>
            </div>
          )}
        </TabsContent>

        {/* MY ORDERS TAB */}
        <TabsContent value="orders" className="space-y-6">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <div>
              <h2 className="text-2xl font-bold">Your Orders</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Track and manage all your food orders
              </p>
            </div>

            <Button
              variant={canReadCanteenLog ? "default" : "outline"}
              onClick={handleViewAllOrders}
              className="flex items-center gap-2"
              disabled={!canReadCanteenLog}
            >
              <Package className="h-4 w-4" />
              {canReadCanteenLog ? "View All Orders" : "Order Log (No Access)"}
            </Button>
          </div>
          {orderLoading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingCart className="h-20 w-20 mx-auto text-gray-400 mb-4" />
              <p className="text-xl text-muted-foreground">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order, index) => (
                <Card key={order.id} className="overflow-hidden">
                  <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer bg-card" onClick={() => toggleOrderDetails(order.id)}>
                    <div className="flex items-start sm:items-center gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                        {orders.length - index}
                      </div>
                      <div>
                        <p className="font-bold text-lg">{order.code ?? "-"}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.order_time).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap justify-between sm:justify-end">
                      <Badge variant={statusConfig[order.status]?.variant || "secondary"} className="text-sm">
                        {React.createElement(statusConfig[order.status]?.icon || Clock, { className: "w-4 h-4 mr-1" })}
                        {statusConfig[order.status]?.label || order.status}
                      </Badge>
                      <span className="font-bold text-xl sm:text-2xl text-primary">₹{order.total_price}</span>
                      {expandedOrders.has(order.id) ? <ChevronUp className="h-6 w-6 text-muted-foreground" /> : <ChevronDown className="h-6 w-6 text-muted-foreground" />}
                    </div>
                  </div>

                  {expandedOrders.has(order.id) && (
                    <CardContent className="p-0 border-t">
                      {detailsLoading[order.id] ? (
                        <div className="flex justify-center items-center py-12 bg-muted/50">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : orderDetails[order.id] ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-b">
                                <TableHead className="whitespace-nowrap">Item</TableHead>
                                <TableHead className="text-center">Qty</TableHead>
                                <TableHead className="text-right">Price</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {orderDetails[order.id].items_detail.map((item) => (
                                <TableRow key={item.id} className="hover:bg-muted/50 transition-colors">
                                  <TableCell className="font-medium">{item.menu_name}</TableCell>
                                  <TableCell className="text-center">{item.quantity}</TableCell>
                                  <TableCell className="text-right font-mono">₹{item.unit_price}</TableCell>
                                  <TableCell className="text-right font-bold text-green-600 dark:text-green-400">₹{item.total_price}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <div className="border-t bg-muted/30 px-6 py-4">
                            <div className="flex justify-end items-center gap-3 font-bold text-lg">
                              <span className="text-muted-foreground">Order Total:</span>
                              <span className="text-primary">₹{order.total_price}</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-red-600 dark:text-red-400 bg-muted/50">
                          <p className="text-lg font-medium">Failed to load order details</p>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* CART SHEET */}
      <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-2xl">Your Cart</SheetTitle>
            <p className="text-lg text-muted-foreground">{cart.length} items • Total: <span className="font-bold text-green-600">₹{getTotal()}</span></p>
          </SheetHeader>
          <ScrollArea className="my-6 h-[60vh] pr-4">
            {cart.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                <ShoppingCart className="h-20 w-20 mx-auto mb-4 opacity-50" />
                <p className="text-xl">Your cart is empty</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl border">
                    <div className="flex-1">
                      <p className="font-bold text-lg">{item.name}</p>
                      <p className="text-sm text-muted-foreground">₹{item.price} each</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button size="icon" variant="outline" onClick={() => updateQty(item.id, -1)}><Minus className="h-4 w-4" /></Button>
                      <span className="w-12 text-center font-bold text-xl">{item.qty}</span>
                      <Button size="icon" variant="outline" onClick={() => updateQty(item.id, 1)}><Plus className="h-4 w-4" /></Button>
                    </div>
                    <Button size="icon" variant="destructive" onClick={() => removeFromCart(item.id)}>
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          {cart.length > 0 && (
            <SheetFooter>
              <div className="w-full space-y-4">
                <div className="flex justify-between text-2xl font-bold">
                  <span>Total</span>
                  <span className="text-green-600">₹{getTotal()}</span>
                </div>
                {/* <Button size="lg" className="w-full text-xl h-14 font-bold" onClick={handlePlaceOrder}>
                  Place Order • ₹{getTotal()}
                </Button> */}
                <Button
                  size="lg"
                  className="w-full text-xl h-14 font-bold"
                  onClick={handlePlaceOrder}
                  disabled={isPlacingOrder || cart.length === 0}
                >
                  {isPlacingOrder ? (
                    <>
                      <Loader2 className="mr-3 h-6 w-6 animate-spin" />
                      Placing Order...
                    </>
                  ) : (
                    <>Place Order • ₹{getTotal()}</>
                  )}
                </Button>
              </div>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* BULK MENU DIALOG */}
      <Dialog open={bulkMenuOpen} onOpenChange={setBulkMenuOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Multiple Menus</DialogTitle></DialogHeader>
          <div className="space-y-6">
            {bulkMenus.map((m, i) => (
              <div key={i} className="p-5 border rounded-lg bg-card space-y-4">
                <div>
                  <Label>Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={m.name}
                    onChange={(e) => updateBulkMenu(i, 'name', e.target.value)}
                    placeholder="Pizza Margherita"
                    className={bulkMenuErrors[i]?.name ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                  {bulkMenuErrors[i]?.name && (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {bulkMenuErrors[i].name}</p>
                  )}
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={m.description} onChange={(e) => updateBulkMenu(i, 'description', e.target.value)} placeholder="Classic cheese pizza" />
                </div>
                <div>
                  <Label>Price <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={m.price}
                    onChange={(e) => updateBulkMenu(i, 'price', e.target.value)}
                    placeholder="49.99"
                    className={bulkMenuErrors[i]?.price ? 'border-red-500 focus-visible:ring-red-500' : ''}
                  />
                  {bulkMenuErrors[i]?.price && (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {bulkMenuErrors[i].price}</p>
                  )}
                </div>
                <div>
                  <Label>Image</Label>
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="w-28 h-28 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center overflow-hidden bg-muted/50">
                        {m.preview ? (
                          <img src={m.preview} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="h-12 w-12 text-muted-foreground/60" />
                        )}
                      </div>
                      {m.preview && (
                        <button
                          type="button"
                          onClick={() => {
                            setBulkMenus(prev => prev.map((item, idx) =>
                              idx === i ? { ...item, profile_img: null, preview: '' } : item
                            ));
                          }}
                          className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1.5 shadow-lg hover:bg-destructive/90 transition"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          if (!file.type.startsWith('image/')) {
                            toast.error('Please select a valid image');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setBulkMenus(prev => prev.map((item, idx) =>
                              idx === i ? { ...item, profile_img: file, preview: reader.result } : item
                            ));
                          };
                          reader.readAsDataURL(file);
                        }}
                        className="hidden"
                        id={`bulk-image-${i}`}
                      />
                      <label htmlFor={`bulk-image-${i}`}>
                        <Button asChild variant="secondary" size="sm">
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            {m.preview ? 'Change Image' : 'Upload Image'}
                          </span>
                        </Button>
                      </label>
                      <p className="text-xs text-muted-foreground">
                        {m.profile_img?.name || 'No file selected'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Label>In Stock</Label>
                  <Switch checked={m.in_stock} onCheckedChange={(v) => updateBulkMenu(i, 'in_stock', v)} />
                </div>
                {bulkMenus.length > 1 && (
                  <Button variant="destructive" size="sm" onClick={() => {
                    setBulkMenus(prev => prev.filter((_, idx) => idx !== i));
                    setBulkMenuErrors(prev => prev.filter((_, idx) => idx !== i));
                  }} className="w-full mt-2">
                    <Trash2 className="mr-2 h-4 w-4" /> Remove This Row
                  </Button>
                )}
              </div>
            ))}
            <Button onClick={addBulkMenuRow} size="sm" variant="outline" className="w-full"><Plus className="mr-2 h-4 w-4" /> Add Another Menu</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setBulkMenus([{ name: '', description: '', price: '', in_stock: true, profile_img: null, preview: '' }]);
              setBulkMenuErrors([{}]);
              setBulkMenuOpen(false);
            }}>Cancel</Button>
            <Button onClick={submitBulkMenus}>Create All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT MENU DIALOG */}
      <Dialog open={!!editMenu} onOpenChange={() => setEditMenu(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Menu</DialogTitle></DialogHeader>
          {editMenu && (
            <div className="space-y-4">
              <div>
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input value={editMenu.name} onChange={e => setEditMenu({ ...editMenu, name: e.target.value })} className={editMenuErrors.name ? 'border-red-500' : ''} />
                {editMenuErrors.name && <p className="text-sm text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {editMenuErrors.name}</p>}
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editMenu.description ?? ''} onChange={e => setEditMenu({ ...editMenu, description: e.target.value })} />
              </div>
              <div>
                <Label>Price <span className="text-red-500">*</span></Label>
                <Input type="number" step="0.01" value={editMenu.price} onChange={e => setEditMenu({ ...editMenu, price: e.target.value })} className={editMenuErrors.price ? 'border-red-500' : ''} />
                {editMenuErrors.price && <p className="text-sm text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {editMenuErrors.price}</p>}
              </div>

              <div>
                <Label>Image</Label>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full border-2 border-dashed border-muted-foreground/40 flex items-center justify-center overflow-hidden bg-muted/50">
                      {menuImagePreview ? (
                        <img src={menuImagePreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : editMenu.profile_img && !menuImageFile ? (
                        <img src={editMenu.profile_img} alt={editMenu.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package className="h-14 w-14 text-muted-foreground/60" />
                      )}
                    </div>

                    {(menuImagePreview || (editMenu.profile_img && menuImageFile !== '')) && (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuImageFile('');
                          setMenuImagePreview('');
                          setEditMenu(prev => ({ ...prev, profile_img: null }));
                        }}
                        className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1.5 shadow-lg hover:bg-destructive/90 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, setMenuImageFile, setMenuImagePreview)}
                      className="hidden"
                      id="edit-menu-image"
                    />
                    <label htmlFor="edit-menu-image">
                      <Button asChild variant="secondary" size="sm">
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          {menuImagePreview || editMenu.profile_img ? 'Change Image' : 'Upload Image'}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {menuImageFile instanceof File
                        ? menuImageFile.name
                        : menuImageFile === ''
                          ? 'Image will be removed'
                          : 'No file selected'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Label>In Stock</Label>
                <Switch checked={editMenu.in_stock} onCheckedChange={v => setEditMenu({ ...editMenu, in_stock: v })} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setEditMenu(null); setEditMenuErrors({}); }}>Cancel</Button>
                <Button onClick={saveMenu}>Save</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ADD INGREDIENTS DIALOG */}
      <Dialog open={!!ingredientMenuId} onOpenChange={() => setIngredientMenuId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Ingredients</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {bulkIngredients.map((ing, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Select value={ing.ingredient} onValueChange={v => updateIngredientRow(i, 'ingredient', v)}>
                    <SelectTrigger className={ingredientErrors[i]?.ingredient ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select ingredient" />
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map(sub => <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {ingredientErrors[i]?.ingredient && <p className="text-sm text-red-500 mt-1"><AlertCircle className="h-3 w-3 inline mr-1" />{ingredientErrors[i].ingredient}</p>}
                </div>
                <div className="w-32">
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={ing.quantity}
                    onChange={e => updateIngredientRow(i, 'quantity', e.target.value)}
                    className={ingredientErrors[i]?.quantity ? 'border-red-500' : ''}
                  />
                  {ingredientErrors[i]?.quantity && <p className="text-sm text-red-500 mt-1"><AlertCircle className="h-3 w-3 inline mr-1" />{ingredientErrors[i].quantity}</p>}
                </div>
              </div>
            ))}
            <Button onClick={addIngredientRow} size="sm" variant="outline" className="w-full"><Plus className="mr-2 h-4 w-4" /> Add Row</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIngredientMenuId(null); setIngredientErrors([{}]); }}>Cancel</Button>
            <Button onClick={submitBulkIngredients}>Add All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT INGREDIENT DIALOG */}
      <Dialog open={!!editIngredient} onOpenChange={() => setEditIngredient(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Ingredient</DialogTitle></DialogHeader>
          {editIngredient && (
            <form onSubmit={async e => {
              e.preventDefault();
              if (!validateEditIngredient()) return;
              try {
                await dispatch(updateIngredient({ id: editIngredient.id, data: { ingredient: editIngredient.ingredient, quantity: editIngredient.quantity } })).unwrap();
                toast.success('Ingredient updated');
                setEditIngredient(null);
              } catch { toast.error('Update failed'); }
            }} className="space-y-4">
              <div>
                <Label>Ingredient <span className="text-red-500">*</span></Label>
                <Select value={editIngredient.ingredient} onValueChange={v => setEditIngredient({ ...editIngredient, ingredient: v })}>
                  <SelectTrigger className={editIngredientErrors.ingredient ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select ingredient" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategories.map(sub => <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {editIngredientErrors.ingredient && <p className="text-sm text-red-500 mt-1"><AlertCircle className="h-3 w-3 inline mr-1" />{editIngredientErrors.ingredient}</p>}
              </div>
              <div>
                <Label>Quantity <span className="text-red-500">*</span></Label>
                <Input type="number" value={editIngredient.quantity} onChange={e => setEditIngredient({ ...editIngredient, quantity: e.target.value })} className={editIngredientErrors.quantity ? 'border-red-500' : ''} />
                {editIngredientErrors.quantity && <p className="text-sm text-red-500 mt-1"><AlertCircle className="h-3 w-3 inline mr-1" />{editIngredientErrors.quantity}</p>}
              </div>
              <DialogFooter>
                <Button type="submit">Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Restore Confirmation Modal */}
      <AlertDialog open={!!restoreMenuId} onOpenChange={() => setRestoreMenuId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore Menu?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            re you sure you want to restore this item? It willl be restore to it's previous state.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRestore}
              disabled={restoreLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {restoreLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Restoring...
                </>
              ) : (
                'Restore'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DELETE CONFIRMATION */}
      <AlertDialog open={!!deleteMenuId} onOpenChange={() => setDeleteMenuId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Menu?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              await dispatch(deleteMenu(deleteMenuId));
              setDeleteMenuId(null);
            }} className="bg-destructive">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteIngredientId} onOpenChange={() => setDeleteIngredientId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Ingredient?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogDescription>Cannot be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              await dispatch(deleteIngredient(deleteIngredientId));
              setDeleteIngredientId(null);
            }} className="bg-destructive">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Recharge Wallet Modal */}
      <Dialog open={walletModalOpen} onOpenChange={setWalletModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Recharge Your Wallet</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col items-center py-6 space-y-6">
            {/* Static image – replace with your real QR code path */}
            <img
              src={qrcode}          // ← change this path
              alt="UPI QR Code for wallet recharge"
              className="w-full h-full object-contain rounded-xl border shadow-md"
            />

          </div>


        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CanteenPage;

